import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import SwipeableViews from 'react-swipeable-views';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';

import { PaginationDots } from 'src/core/components/pagination/pagination-dots';

import styles from './desktop/estimate-item-images.less';

interface Props {
    imageUris: string[] | undefined;
}

@observer
export class EstimateItemImages extends React.Component<Props> {
    @observable currentIndex = 0;
    @observable isImageLightBoxOpen = false;
    @observable imageLightBoxIndex = this.currentIndex;
    @computed get imageUris() {
        return this.props.imageUris;
    }

    private mousePos: { x: number | null; y: number | null } | undefined;

    handleChangeIndex = (index: number) => {
        this.currentIndex = index;
    };

    openImageLightBox = () => {
        this.isImageLightBoxOpen = true;
        this.imageLightBoxIndex = this.currentIndex;
    };

    closeImageLightBox = () => {
        this.isImageLightBoxOpen = false;
    };

    gotoPrev = () => {
        if (!this.imageUris) return;
        this.imageLightBoxIndex =
            (this.imageLightBoxIndex + this.imageUris.length - 1) % this.imageUris.length;
    };

    gotoNext = () => {
        if (!this.imageUris) return;
        this.imageLightBoxIndex = (this.imageLightBoxIndex + 1) % this.imageUris.length;
    };

    /** Replaced onClick with onMouseDown/handleMouseMoveOrUp to avoid swipe and click both triggering */
    handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        this.mousePos = { x: null, y: null };
        window.addEventListener('mousemove', this.handleMouseMoveOrUp);
        window.addEventListener('mouseup', this.handleMouseMoveOrUp);
    };

    private handleMouseMoveOrUp = (event: MouseEvent) => {
        if (!this.mousePos) {
            return;
        }

        if (this.mousePos.x === null || this.mousePos.y === null) {
            this.mousePos.x = event.x;
            this.mousePos.y = event.y;
        } else if (
            Math.abs(this.mousePos.x - event.x) > 20 ||
            Math.abs(this.mousePos.y - event.y) > 20
        ) {
            // If mouse moves > 20px, cancel
            this.mouseCleanup();
            return;
        }

        if (event.type === 'mouseup') {
            // Trigger "click"
            this.mouseCleanup();
            this.openImageLightBox();
        }
    };

    private mouseCleanup() {
        this.mousePos = undefined;
        window.removeEventListener('mousemove', this.handleMouseMoveOrUp);
        window.removeEventListener('mouseup', this.handleMouseMoveOrUp);
    }

    render() {
        if (!this.imageUris) {
            return null;
        }
        const images = this.imageUris;
        const imageIndex = this.imageLightBoxIndex;

        return (
            <div className={styles.main}>
                <SwipeableViews
                    index={this.currentIndex}
                    onChangeIndex={this.handleChangeIndex}
                    enableMouseEvents
                >
                    {images.map((uri) => (
                        <div
                            key={uri}
                            className={styles.wrapper}
                            onMouseDown={this.handleMouseDown}
                        >
                            <img className={styles.img} src={uri} />
                        </div>
                    ))}
                </SwipeableViews>
                {images.length > 1 && (
                    <PaginationDots
                        className={styles.pagination}
                        small
                        dots={images.length}
                        index={this.currentIndex}
                        onChangeIndex={this.handleChangeIndex}
                    />
                )}
                {images && this.isImageLightBoxOpen && (
                    <Lightbox
                        mainSrc={images[imageIndex]}
                        prevSrc={images[(imageIndex + images.length - 1) % images.length]}
                        nextSrc={images[(imageIndex + 1) % images.length]}
                        onCloseRequest={this.closeImageLightBox}
                        onMovePrevRequest={this.gotoPrev}
                        onMoveNextRequest={this.gotoNext}
                    />
                )}
            </div>
        );
    }
}
